import React from 'react'
import {NavLink} from 'react-router-dom'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Slide from '@mui/material/Slide'
import useScrollTrigger from '@mui/material/useScrollTrigger'
import useMediaQuery from '@mui/material/useMediaQuery'
import {useTheme} from '@mui/material/styles'
import MenuIcon from '@mui/icons-material/Menu'
import AccountCircle from '@mui/icons-material/AccountCircle'
import useUser from '../hooks/useUser'
import {equistampButton} from '../components/text'
import Path from '../routeLinks'

type HeaderProps = {
  title?: string
}

type HeaderLinkProps = {
  text: string
  to: string
  variant?: string
  external?: boolean
}
const HeaderLink = ({text, to, variant, external}: HeaderLinkProps) => (
  <Button
    component={external ? 'a' : NavLink}
    to={external ? undefined : to}
    href={external ? to : undefined}
    variant={(variant || 'text') as any}
    color={variant === 'contained' ? 'primary' : 'inherit'}
    style={{fontWeight: 600}}
    sx={{
      ...equistampButton,
      mb: 'inherit',
      minWidth: 'inherit',
      m: 0.3,
      ':hover': {
        backgroundColor: variant === 'contained' ? 'secondary.dark' : null,
      },
    }}
  >
    {text}
  </Button>
)

const LINKS = [
  {to: Path.evaluations.all(), text: 'Evaluations', type: 'always'},
  {to: Path.models.all(), text: 'Models', type: 'always'},
  {to: Path.about(), text: 'About', type: 'always'},
  {to: Path.pricing(), text: 'Pricing', type: 'always'},
  {to: Path.docs(), text: 'Docs', type: 'always', external: true},
  {to: Path.admin.index(), text: 'Admin Panel', type: 'admin'},

  {to: Path.user.login(), text: 'Log in', type: 'loggedOut', variant: 'contained'},
  {to: Path.user.register(), text: 'Register', type: 'loggedOut', variant: 'contained'},

  {to: Path.user.me(), text: 'Profile', type: 'loggedIn'},
  {to: Path.user.logout(), text: 'Logout', type: 'loggedIn'},
]

const NormalLinks = ({loggedIn, isAdmin}: {loggedIn: boolean; isAdmin: boolean}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  return (
    <>
      {LINKS.filter(
        (item) => item.type === 'always' || (!loggedIn && item.type === 'loggedOut')
      ).map((item) => (
        <HeaderLink key={item.to} {...item} />
      ))}

      {isAdmin &&
        LINKS.filter((item) => item.type === 'admin').map((item) => (
          <HeaderLink key={item.to} {...item} />
        ))}

      {loggedIn && (
        <>
          <div>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={!!anchorEl}
              onClose={() => setAnchorEl(null)}
            >
              {LINKS.filter((item) => item.type === 'loggedIn').map((item) => (
                <MenuItem key={item.to} component={NavLink} to={item.to}>
                  <Typography style={{fontWeight: 600}}>{item.text}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </div>
        </>
      )}
    </>
  )
}

const MobileLinks = ({loggedIn, isAdmin}: {loggedIn: boolean; isAdmin: boolean}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  return (
    <>
      <Box>
        <IconButton
          size="large"
          aria-label="menu"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          color="inherit"
        >
          <MenuIcon />
        </IconButton>
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={!!anchorEl}
          onClose={() => setAnchorEl(null)}
        >
          {LINKS.filter((item) =>
            ['always', isAdmin ? 'admin' : '', loggedIn ? 'loggedIn' : 'loggedOut'].includes(
              item.type
            )
          ).map(({to, text, external}) => (
            <MenuItem
              key={to}
              component={external ? 'a' : NavLink}
              to={external ? undefined : to}
              href={external ? to : undefined}
            >
              <Typography style={{fontWeight: 600}}>{text}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </>
  )
}

function HideOnScroll({children}: {children: React.ReactElement}) {
  const trigger = useScrollTrigger({})
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  )
}

const Header = ({title}: HeaderProps) => {
  const {loggedIn, isAdmin} = useUser()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (title) {
    document.title = title
  }

  return (
    <HideOnScroll>
      <AppBar component="nav" elevation={0} color="inherit" enableColorOnDark position="relative">
        <Toolbar>
          <Box
            component={NavLink}
            to={Path.home()}
            alignItems="center"
            display="flex"
            sx={{flexGrow: 1}}
          >
            <img src="/logoBlue.png" alt="Equistamp" width="224px" height="40px" />
          </Box>

          {isMobile ? (
            <MobileLinks loggedIn={loggedIn} isAdmin={isAdmin} />
          ) : (
            <NormalLinks loggedIn={loggedIn} isAdmin={isAdmin} />
          )}
        </Toolbar>
      </AppBar>
    </HideOnScroll>
  )
}

export default Header
